<template>
  <div class="modal-overlay" @click.self="close">
    <div class="modal-content setup-modal">
      <!-- Modal Header -->
      <div class="modal-header-premium">
        <div class="header-icon">
          <i class="fas" :class="setupComplete ? 'fa-check-circle' : 'fa-shield-alt'"></i>
        </div>
        <div class="header-text">
          <h4>{{ setupComplete ? 'Setup Complete' : 'Secure Your Account' }}</h4>
          <p>{{ setupComplete ? 'Two-Factor Authentication is now active' : 'Enable Two-Factor Authentication' }}</p>
        </div>
        <button class="close-btn" @click="close">&times;</button>
      </div>

      <div class="modal-body-premium">
        <!-- Step 1: Scan & Enter Code -->
        <div v-if="!setupComplete" class="setup-steps">
          <div class="step-guide">
            <span class="step-num">1</span>
            <p>Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)</p>
          </div>

          <div class="qr-container">
            <div v-if="qrCodeSvg" class="qr-frame" v-html="qrCodeSvg"></div>
            <div v-else class="qr-placeholder">
              <div class="spinner-border text-primary"></div>
            </div>
          </div>

          <div class="manual-entry">
            <p class="label">Can't scan? Use this secret key:</p>
            <div class="secret-box">
              <code class="secret-text">{{ secret }}</code>
              <button class="copy-btn" @click="copySecret" title="Copy Secret">
                <i class="far fa-copy"></i>
              </button>
            </div>
          </div>

          <div class="step-guide mt-4">
            <span class="step-num">2</span>
            <p>Verification: Enter the 6-digit code from your app</p>
          </div>

          <div class="code-input-wrapper">
            <o-input-digit 
              v-model="code" 
              placeholder="000000" 
              :maxlength="6" 
              class="modern-digit-input"
              @keyup.enter="handleConfirm" 
            />
          </div>

          <div v-if="error" class="error-feedback">
            <i class="fas fa-exclamation-circle me-1"></i> {{ error }}
          </div>
        </div>

        <!-- Step 2: Recovery Codes -->
        <div v-else class="success-flow">
          <div class="success-message">
            <p>Your account is now protected. Please save these recovery codes in a safe place. They are the only way to access your account if you lose your device.</p>
          </div>

          <div class="recovery-codes-grid">
            <div v-for="(item, index) in recoveryCodes" :key="index" class="code-item">
              {{ item.code || item }}
            </div>
          </div>

          <div class="safety-warning">
            <i class="fas fa-lock me-2"></i> Keep these codes offline or in a secure password manager.
          </div>
        </div>
      </div>

      <!-- Modal Footer -->
      <div class="modal-footer-premium">
        <template v-if="!setupComplete">
          <button class="btn-secondary-clean" @click="close">Cancel</button>
          <button 
            class="btn-primary-premium" 
            @click="handleConfirm" 
            :disabled="!code || code.length < 6 || loading"
          >
            <i v-if="loading" class="fas fa-spinner fa-spin me-2"></i>
            {{ loading ? 'Verifying...' : 'Finish Setup' }}
          </button>
        </template>
        <template v-else>
          <button class="btn-secondary-clean" @click="downloadCodes">
            <i class="fas fa-download me-2"></i> Download Codes
          </button>
          <button class="btn-primary-premium" @click="close">
            Done
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { OInputDigit } from '@/components/OmnihControl';

/**
 * Handles the 2FA setup process, including QR code display and confirmation.
 * Follows a clean, premium design pattern with clear user guidance.
 */
export default {
    name: 'TwoFactorSetup',
    components: { OInputDigit },
    data() {
        return {
            code: '',
            loading: false,
            error: '',
            setupComplete: false
        };
    },
    computed: {
        ...mapState('TwoFactor', ['qrCodeSvg', 'secret', 'recoveryCodes'])
    },
    mounted() {
        this.fetchSetup();
    },
    methods: {
        ...mapActions('TwoFactor', ['setup', 'confirm']),
        
        async fetchSetup() {
            try {
                await this.setup();
            } catch (err) {
                this.error = 'Failed to load QR code. Please try again.';
            }
        },
        
        async handleConfirm() {
            if (!this.code || this.code.length < 6) return;
            
            this.loading = true;
            this.error = '';
            
            try {
                await this.$store.dispatch('TwoFactor/confirm', this.code);
                this.setupComplete = true;
            } catch (err) {
                this.error = err.response?.data?.message || 'Invalid code. Please try again.';
            } finally {
                this.loading = false;
            }
        },
        
        copySecret() {
            if (!this.secret) return;
            navigator.clipboard.writeText(this.secret).then(() => {
                // Could show a tooltip or small toast here if needed
                alert('Secret key copied to clipboard');
            }).catch(err => {
                console.error('Failed to copy: ', err);
            });
        },
        
        downloadCodes() {
            if (!this.recoveryCodes || !this.recoveryCodes.length) return;
            const codes = this.recoveryCodes.map(c => c.code || c).join('\n');
            const blob = new Blob([codes], { type: 'text/plain' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'recovery-codes.txt';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        },
        
        close() {
            this.$emit('close');
        }
    }
}
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(4px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1100;
}

.setup-modal {
    background: white;
    width: 90%;
    max-width: 550px;
    border-radius: 16px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    display: flex;
    flex-direction: column;
    max-height: 90vh;
}

.modal-header-premium {
    padding: 24px;
    border-bottom: 1px solid #f3f4f6;
    display: flex;
    align-items: flex-start;
    gap: 16px;
    position: relative;
    background: #f9fafb;
    border-radius: 16px 16px 0 0;
}

.header-icon {
    width: 48px;
    height: 48px;
    background: var(--secondary-color, rgba(252, 102, 102, 0.1));
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--primary-color, #fc6666);
    font-size: 20px;
}

.header-text h4 {
    margin: 0 0 4px 0;
    font-size: 18px;
    font-weight: 700;
    color: #111827;
}

.header-text p {
    margin: 0;
    font-size: 14px;
    color: #6b7280;
}

.close-btn {
    position: absolute;
    top: 20px;
    right: 20px;
    background: none;
    border: none;
    font-size: 24px;
    color: #9ca3af;
    cursor: pointer;
    transition: color 0.2s;
}

.close-btn:hover {
    color: #4b5563;
}

.modal-body-premium {
    padding: 32px;
    overflow-y: auto;
}

.setup-steps {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.step-guide {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 24px;
    width: 100%;
}

.step-num {
    width: 28px;
    height: 28px;
    background: #111827;
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 700;
    flex-shrink: 0;
}

.step-guide p {
    margin: 0;
    font-size: 15px;
    color: #374151;
    font-weight: 500;
}

.qr-container {
    background: white;
    padding: 16px;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    margin-bottom: 24px;
    display: flex;
    justify-content: center;
}

.qr-placeholder {
    width: 200px;
    height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f9fafb;
}

.manual-entry {
    background: #f9fafb;
    padding: 16px;
    border-radius: 8px;
    width: 100%;
    margin-bottom: 32px;
}

.manual-entry .label {
    font-size: 13px;
    color: #6b7280;
    margin-bottom: 8px;
    text-align: center;
}

.secret-box {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background: white;
    padding: 8px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
}

.secret-text {
    font-family: monospace;
    font-size: 16px;
    color: #111827;
    font-weight: 600;
    letter-spacing: 1px;
}

.copy-btn {
    background: none;
    border: none;
    color: #6b7280;
    cursor: pointer;
    font-size: 16px;
    padding: 4px;
}

.copy-btn:hover {
    color: var(--primary-color, #fc6666);
}

.code-input-wrapper {
    margin-bottom: 24px;
    display: flex;
    justify-content: center;
}

.error-feedback {
    color: #ef4444;
    font-size: 14px;
    background: #fef2f2;
    padding: 10px 16px;
    border-radius: 8px;
    margin-top: 16px;
}

.success-flow {
    text-align: center;
}

.success-message {
    margin-bottom: 24px;
    color: #374151;
    line-height: 1.6;
}

.recovery-codes-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    margin-bottom: 24px;
    background: #f9fafb;
    padding: 16px;
    border-radius: 12px;
    border: 1px solid #f3f4f6;
}

.code-item {
    font-family: monospace;
    font-size: 15px;
    color: #111827;
    font-weight: 600;
    text-align: center;
    padding: 8px;
    background: white;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
}

.safety-warning {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    color: #d97706;
    background: #fffbeb;
    padding: 12px;
    border-radius: 8px;
}

.modal-footer-premium {
    padding: 24px;
    border-top: 1px solid #f3f4f6;
    background: #f9fafb;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    border-radius: 0 0 16px 16px;
}

.btn-secondary-clean {
    padding: 10px 20px;
    background: white;
    border: 1px solid #d1d5db;
    color: #374151;
    border-radius: 8px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-secondary-clean:hover {
    background: #f3f4f6;
}

.btn-primary-premium {
    padding: 10px 24px;
    background: var(--primary-color, #fc6666);
    border: none;
    color: white;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 4px 6px -1px var(--secondary-color, rgba(252, 102, 102, 0.25));
    transition: all 0.2s;
}

.btn-primary-premium:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
}

.btn-primary-premium:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
}
</style>
